import React, { useState, useEffect } from "react";
import { DataTable, Badge, TableActionButton, InlineLoader } from "@/components/ui";
import { showApiError, showSuccessMessage } from "@/lib/toast";
import { statusRevertService } from "../../verve-contacts/services/statusRevertService";
import { CONTACT_STAGES } from "../../verve-contacts/constants/contactConstants";
import { LEAD_STAGES } from "@/constants/globalConstants";
import { Check, X } from "lucide-react";
import { FilterSection } from "@/components/ui/FilterSection";

export default function AdditionalApprovalsTab() {
  const [requests, setRequests] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [processingId, setProcessingId] = useState(null);
  const [search, setSearch] = useState("");
  const [filters, setFilters] = useState({ date: "all", fromDate: "", toDate: "", status: "pending" });

  const STATUS_OPTIONS = [
    { value: "pending", label: "Pending" },
    { value: "approved", label: "Approved" },
    { value: "rejected", label: "Rejected" }
  ];

  const handleSetFilters = (key, value) => {
    setFilters(prev => ({ ...prev, [key]: value }));
  };

  const fetchRequests = async () => {
    try {
      setIsLoading(true);
      const res = await statusRevertService.getPendingRequests();
      setRequests(res.data?.data || []);
    } catch (error) {
      showApiError(error);
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    fetchRequests();
  }, []);

  const handleReview = async (id, status) => {
    try {
      setProcessingId(id);
      await statusRevertService.reviewRequest(id, status);
      showSuccessMessage(`Request ${status} successfully.`);
      fetchRequests();
    } catch (error) {
      showApiError(error);
    } finally {
      setProcessingId(null);
    }
  };

  const getStageLabel = (key) => {
    let stage = CONTACT_STAGES.find(s => s.key === key);
    if (!stage) {
      stage = LEAD_STAGES.find(s => s.key === key);
    }
    return stage ? stage.label : key;
  };

  const filteredRequests = React.useMemo(() => {
    let result = [...requests];

    if (search) {
      const term = search.toLowerCase();
      result = result.filter(r => {
        const record = r.contactId;
        const name = `${record?.customerName || ''} ${record?.firstName || ''} ${record?.lastName || ''}`.toLowerCase();
        return (
          name.includes(term) ||
          (record?.customId || '').toLowerCase().includes(term) ||
          (r.reason || '').toLowerCase().includes(term)
        );
      });
    }

    const { date, fromDate, toDate, status } = filters;

    if (status && status !== "all") {
      result = result.filter(r => String(r.status || "pending").toLowerCase() === String(status).toLowerCase());
    }

    if (date !== "all" || fromDate || toDate) {
      let startDate = null;
      let endDate = null;

      const now = new Date();
      if (date && date !== "all") {
        startDate = new Date();
        switch (date) {
          case "today": startDate.setHours(0, 0, 0, 0); break;
          case "this_week": case "week": startDate.setDate(now.getDate() - 7); break;
          case "this_month": case "month": startDate.setDate(now.getDate() - 30); break;
          case "this_year": case "year": startDate.setDate(now.getDate() - 365); break;
        }
        endDate = now;
      } else {
        if (fromDate) startDate = new Date(fromDate);
        if (toDate) endDate = new Date(toDate);
      }

      if (startDate || endDate) {
        result = result.filter((r) => {
          const createdAt = new Date(r.createdAt);
          if (startDate && createdAt < startDate) return false;
          if (endDate) {
            const end = new Date(endDate);
            end.setHours(23, 59, 59, 999);
            if (createdAt > end) return false;
          }
          return true;
        });
      }
    }

    return result;
  }, [requests, search, filters]);

  const columns = [
    {
      key: "contact",
      label: "Contact Details",
      render: (req) => {
        const record = req.contactId;
        const name = record?.customerName || `${record?.firstName || ""} ${record?.lastName || ""}`;
        return (
          <div>
            <p className="font-medium text-slate-800">{name}</p>
            <p className="text-xs text-slate-500">{record?.customId || "No ID"}</p>
          </div>
        );
      }
    },
    {
      key: "requestedBy",
      label: "Requested By",
      render: (req) => (
        <div>
          <p className="font-medium text-slate-800">{req.requestedBy?.name || "Unknown"}</p>
          <p className="text-xs text-slate-500 capitalize">{req.requestedBy?.role || "Staff"}</p>
        </div>
      )
    },
    {
      key: "revertDetails",
      label: "Revert Request",
      render: (req) => (
        <div className="flex flex-col gap-1">
          <div className="flex items-center gap-2 text-sm">
            <Badge variant="neutral">{getStageLabel(req.currentStatus)}</Badge>
            <span className="text-slate-400">→</span>
            <Badge variant="warning">{getStageLabel(req.targetStatus)}</Badge>
          </div>
        </div>
      )
    },
    {
      key: "reason",
      label: "Reason / Notes",
      render: (req) => (
        <p className="text-sm text-slate-600 max-w-[250px] truncate" title={req.reason}>
          {req.reason}
        </p>
      )
    },
    {
      key: "actions",
      label: "Actions",
      render: (req) => (
        <div className="flex items-center gap-2">
          {processingId === req._id ? (
            <InlineLoader size="sm" />
          ) : (
            <>
              <TableActionButton 
                icon={Check} 
                onClick={() => handleReview(req._id, "approved")}
                tooltip="Approve Revert"
                className="text-emerald-600 bg-emerald-50 hover:bg-emerald-100"
              />
              <TableActionButton 
                icon={X} 
                onClick={() => handleReview(req._id, "rejected")}
                tooltip="Reject Revert"
                className="text-rose-600 bg-rose-50 hover:bg-rose-100"
              />
            </>
          )}
        </div>
      )
    }
  ];

  if (isLoading) {
    return (
      <div className="flex justify-center items-center h-40 bg-white rounded-xl shadow-sm border border-slate-200/60">
        <InlineLoader size="lg" />
      </div>
    );
  }

  return (
    <div className="flex flex-col gap-2">
      <div className="flex flex-col rounded-2xl overflow-hidden bg-white shadow-[0_8px_30px_rgb(0,0,0,0.04)] min-w-0">
        <FilterSection
          search={search}
          setSearch={setSearch}
          filters={filters}
          setFilters={handleSetFilters}
          showExport={false}
          statusOptions={STATUS_OPTIONS}
          statusPlaceholder="All Statuses"
        />
      </div>

      <div className="bg-white rounded-xl shadow-sm border border-slate-200/60 overflow-hidden">
        <DataTable
          columns={columns}
          data={filteredRequests}
          emptyMessage="No pending revert requests."
        />
      </div>
    </div>
  );
}
